import React, { useEffect, useState } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import {
  Card, Spin, Row, Col, message, notification, Modal,
  Form,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import polylabel from 'polylabel';
import GeoMapShapefiles from './GeoMapShapefiles';
import ImportShapefilesHeader from './ImportShapefilesHeader';
import ImportShapefilesFirstStage from './ImportShapefilesFirstStage';
import ImportShapefilesSecondStage from './ImportShapefilesSecondStage';
import ImportShapefilesThirdStage from './ImportShapefilesThirdStage';
import {
  get, patch, post, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { namespaces } from '../../../i18n/i18n.constants';
import { backendResponseCodes } from '../../../utils/responseMessages';
import { checkIfAPolygonIsSimple, calculatePolygonAreaInHectare } from '../../../utils/GeometryUtils';
import './ImportShapefiles.scss';
import { SessionExpiredException } from '../../../utils/errors';

const ImportShapefiles = (): JSX.Element => {
  const { t } = useTranslation('namespaces');
  const navigate = useNavigate();

  const [state, setState] = useState<boolean>(true);

  // 3 etapas - controlar as telas
  const [stage, setStage] = useState<number>(1);

  const [forceRerender, setForceRerender] = useState<number>(1);

  // zoom do mapa
  const [zoom, setZoom] = useState<number>(16);

  // contralizar o mapa
  const [mapCenter, setMapCenter] = useState([]);

  // talhão selecionado no mapa
  const [clickedShape, setClickedShape] = useState({});

  // talhões cadastrados + talhões dos arquivos
  const [shapes, setShapes] = useState([]);

  // loading na requisiçao de validar matrícula
  const [validatingMatriculation, setValidatingMatriculation] = useState<boolean>(false);

  // loading para as telas
  const [loadingScreen, setLoadingScreen] = useState({
    active: false,
    text: '',
  });

  // matrícula digitada
  const [matriculation, setMatriculation] = useState({
    value: '',
    error: false,
    errorMessage: '',
    status: '',
  });

  // talhões dos arquivos
  const [fileShapes, setFileShapes] = useState([]);

  // propriedade selecionada
  const [land, setLand] = useState({
    id: null,
    value: '',
    location: [],
    error: false,
    errorMessage: '',
    status: '',
  });

  // opções das propriedades no Select
  const [landOptions, setLandOptions] = useState([]);

  // opções das culturas no Select
  const [culturesOptions, setCulturesOptions] = useState([]);

  // arquivo(s) informado(s) na etapa 1
  const [allFiles, setAllfiles] = useState([]);

  // arquivo(s) informado(s) na etapa 1
  const [shapefiles] = useState([]);

  // retorno dos arquivos que foram importados
  const [fileSuccess, setFileSuccess] = useState([]);

  // retorno dos arquivos que não foram importados
  const [fileErrors, setFileErrors] = useState([]);

  // quando o usuário não informa o(s) arquivo(s)
  const [errorShapefiles, setErrorShapefiles] = useState<boolean>(false);

  const handleLoadingScreen = (active: boolean, text: string): void => {
    setLoadingScreen({ active, text });
  };

  const handleModal = (type: number|string): void => {
    switch (type) {
      case -9000:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'Tamanho do arquivo excedido',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -9001:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'Tipo de arquivo não suportado',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -9004:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'Todos os arquivos excedem o tamanho limite',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -9010:
        Modal.warning({
          title: 'Erro na importação dos arquivos!',
          content: 'Verifique os arquivos inseridos e tente novamente.',
          okText: 'Ok',
          autoFocusButton: null,
          icon: (<WarningOutlined />),
        });

        break;

      case -13002:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'Um dos arquivos KML está vazio',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -13003:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'Um dos arquivos KMZ está vazio',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -13000:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'O arquivo não possui a tag "linear ring".',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case -13001:
        Modal.error({
          title: 'Erro na importação dos arquivos!',
          content: 'O arquivo não possui a tag "coordinates".',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;

      case 9006:
        Modal.warning({
          title: 'Erro na importação de algum ou alguns dos arquivos!',
          content: 'Os arquivos com erro não foram importados.',
          okText: 'Ok',
          autoFocusButton: null,
          icon: (<WarningOutlined />),
        });
        break;

      case 'cancel':
        if (matriculation.value || land.value || allFiles.length) {
          Modal.confirm({
            title: 'Os dados alterados serão perdidos!',
            content: 'Tem certeza que deseja cancelar?',
            okText: 'Sim, cancelar sem salvar',
            cancelText: 'Não',
            autoFocusButton: null,
            icon: (<WarningOutlined />),
            onOk: () => navigate('/home'),
          });
        } else {
          navigate('/home');
        }
        break;
      case 9007: break;
      case 9008: break;
      case 2003: break;
      case 9005: break;
      case 9002: break;
      default:
        Modal.error({
          title: 'Não foi possível concluir a requisição',
          content: 'Ocorreu um erro ao importar os arquivos.',
          okText: 'Ok',
          autoFocusButton: null,
        });
        break;
    }
  };

  const handleStateField = (field: string, value: any): void => {
    switch (field) {
      case 'forceRerender':
        setForceRerender((previous) => {
          return (previous + 1);
        });
        break;

      case 'stage':
        setStage(value);
        break;

      case 'zoom':
        setZoom(value);
        break;

      case 'mapCenter':
        setMapCenter(value);
        break;

      case 'clickedShape':
        setClickedShape(value);
        break;

      case 'shapes':
        setShapes(value);
        break;

      case 'allfiles':
        setAllfiles(value);
        break;

      case 'validatingMatriculation':
        setValidatingMatriculation(value);
        break;

      case 'loadingScreen':
        setLoadingScreen(value);
        break;

      case 'matriculation':
        setMatriculation(value);
        break;

      case 'fileShapes':
        setFileShapes(value);
        break;

      case 'land':
        setLand(value);
        break;

      case 'landOptions':
        setLandOptions(value);
        break;

      case 'culturesOptions':
        setCulturesOptions(value);
        break;

      case 'fileSuccess':
        setFileSuccess(value);
        break;

      case 'fileErrors':
        setFileErrors(value);
        break;

      case 'errorShapefiles':
        setErrorShapefiles(value);
        break;

      default:
        throw new Error(`variavel de state nao encontrado: "${field}"`);
    }
  };

  const handleFileException = (error): void => {
    if (error.body) {
      const { body } = error;
      handleLoadingScreen(false, '');
      handleModal(body?.code);
    } else if (error.message) {
      const parsedMessage = JSON.parse(error.message);
      handleLoadingScreen(false, '');

      if (parsedMessage.code) {
        handleModal(parsedMessage.code);
      } else {
        handleModal('');
      }
    } else {
      handleModal('');
    }
  };

  const onSubmitToSecondStage = async (): Promise<void> => {
    let newErrorShapefiles = false;
    let successRequest = false;
    let responseShape;
    let responseKML;
    let responseKMZ;
    const kmzFiles = [];
    const KMLfiles = [];
    const newCulturesOptions = [];

    if (!matriculation.value) {
      matriculation.error = true;
      matriculation.errorMessage = 'Infome um BP.';
      matriculation.status = 'error';
    }

    if (!land.value) {
      land.error = true;
      land.errorMessage = 'Infome uma propriedade';
      land.status = 'error';
    }

    if (!land.error) {
      try {
        const requestRef: RequestWrapper = get({
          url: `/land/retrieveCulturesByLand?landId=${land.id}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        const responseCulture = await requestRef;

        responseCulture?.body.cultures.forEach((culture) => newCulturesOptions.push(culture));

        setCulturesOptions(newCulturesOptions);
      } catch (error: unknown) {
        if (!(error instanceof SessionExpiredException)) {
          message.error('Erro ao carregar as opções de culturas');
        }
      }
    }

    if (!allFiles.length) {
      newErrorShapefiles = true;
    }

    setMatriculation(matriculation);
    setLand(land);
    setErrorShapefiles(newErrorShapefiles);

    if (!matriculation.error && !land.error && !newErrorShapefiles) {
      handleLoadingScreen(true, 'Carregando...');

      const landShapes = [];
      const newFileShapes = [];
      const filesSuccess = [];

      allFiles.forEach((e) => {
        switch (true) {
          case e.type.toLowerCase() === '.kmz':
          case e.type.toLowerCase() === 'application/vnd.google-earth.kmz':
          case e.name.toLowerCase().includes('.kmz'):
            kmzFiles.push(e);
            break;

          case e.name.toLowerCase().includes('.kml'):
          case e.type.toLowerCase() === '.kml':
            KMLfiles.push(e);
            break;

          case e.type.toLowerCase() === '.rar':
          case e.type.toLowerCase() === '.zip':
          case e.name.toLowerCase().includes('.rar'):
          case e.name.toLowerCase().includes('.zip'):
          case e.type.toLowerCase() === 'application/zip':
          case e.type.toLowerCase() === 'application/rar':
          case e.type.toLowerCase() === 'application/x-zip-compressed':
            shapefiles.push(e);
            break;

          default:
            break;
        }
      });

      if (shapefiles.length > 0) {
        try {
          const formData = new FormData();

          shapefiles.forEach((file: File) => {
            formData.append('shapefiles', file);
          });

          formData.append('id', land.id);

          const requestRef: RequestWrapper = patch({
            url: '/land/importShapefiles',

            params: formData,

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          responseShape = await requestRef;

          if (!responseShape?.body.error) {
            handleModal(responseShape?.body.code);

            successRequest = true;

            responseShape?.body.land.landFields.forEach((landField, index) => {
              const polygon = landField.polygon.map((element) => ([element.lat, element.lon]));
              const polygonCenter = polylabel([polygon], 1.0);

              landShapes.push({
                key: `shapeland_${index}`,
                origin: 'shapeland',
                polygonCenter,
                area: calculatePolygonAreaInHectare(landField.polygon),
                isDeleted: false,
                isSelected: false,
                polygon,
              });
            });

            responseShape?.body.geometriesShpOutput.forEach((shape) => {
              const polygon = shape.coordinates.map((element) => ([element.lat, element.lon]));
              const polygonCenter = polylabel([polygon], 1.0);
              let foundCulture;
              let finalCultureString;
              let finalCultureId;

              if (shape.culture !== null && shape.culture !== '') {
                foundCulture = newCulturesOptions.find(
                  (element) => element.description
                    .toLowerCase() === shape.culture.toLowerCase(),
                );
              }

              if (foundCulture) {
                finalCultureString = `::${foundCulture.id}::${foundCulture.description}`;
                finalCultureId = foundCulture.id;
              }

              newFileShapes.push({
                key: shape.temporaryId,
                origin: 'shapefile',
                polygonCenter,
                area: calculatePolygonAreaInHectare(shape.coordinates),
                isDeleted: false,
                isSelected: false,
                polygon,

                form: {
                  name: {
                    value: shape.landFieldName || '',
                    error: '',
                  },

                  culture: {
                    value: shape.culture ? finalCultureString : '',
                    id: shape.culture ? finalCultureId : null,
                    error: '',
                  },

                  details: {
                    value: shape.note || '',
                  },
                },
              });
            });
            filesSuccess.push(responseShape?.body.fileSuccess);
          }
        } catch (error: unknown) {
          if (!(error instanceof SessionExpiredException)) {
            handleFileException(error);
          }
          handleLoadingScreen(false, '');
        }
      }

      if (KMLfiles.length > 0) {
        try {
          const formData = new FormData();

          KMLfiles.forEach((file: File) => {
            formData.append('kml', file);
          });

          formData.append('id', land.id);

          const requestRef: RequestWrapper = patch({
            url: '/land/importKml',

            params: formData,

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          responseKML = await requestRef;

          if (!responseKML?.body.error) {
            handleModal(responseKML?.body.code);

            successRequest = true;

            responseKML?.body.land.landFields.forEach((landField, index) => {
              const polygon = landField.polygon.map((element) => ([element.lat, element.lon]));
              const polygonCenter = polylabel([polygon], 1.0);

              landShapes.push({
                key: `shapeland_${index + landShapes.length}`,
                origin: 'shapeland',
                polygonCenter,
                area: calculatePolygonAreaInHectare(landField.polygon),
                isDeleted: false,
                isSelected: false,
                polygon,
              });
            });

            responseKML?.body.geometriesOutput.forEach((shape, index) => {
              const polygon = shape.coordinates.map((element) => ([element.lat, element.lon]));

              const polygonCenter = polylabel([polygon], 1.0);

              let foundCulture;
              let finalCultureString;
              let finalCultureId;

              if (responseKML?.body.cultureList[index] != null && responseKML?.body.cultureList[index] != '') {
                foundCulture = newCulturesOptions.find(
                  (element) => element.description
                    .toLowerCase() === responseKML?.body.cultureList[index].toLowerCase(),
                );
              }

              if (foundCulture) {
                finalCultureString = `::${foundCulture.id}::${foundCulture.description}`;
                finalCultureId = foundCulture.id;
              }

              newFileShapes.push({
                key: shape.temporaryId + newFileShapes.length,
                origin: 'shapefile',
                polygonCenter,
                area: calculatePolygonAreaInHectare(shape.coordinates),
                isDeleted: false,
                isSelected: false,
                polygon,

                form: {
                  name: {
                    value: responseKML?.body.landFieldNameList[index] || '',
                    error: '',
                  },

                  culture: {
                    value: responseKML?.body.cultureList[index] ? finalCultureString : '',
                    id: responseKML?.body.cultureList[index] ? finalCultureId : null,
                    error: '',
                  },

                  details: {
                    value: responseKML?.body.noteList[index] || '',
                  },
                },
              });
            });
            filesSuccess.push(responseKML?.body.fileSuccess);
          }
        } catch (error: unknown) {
          console.log('error', error);
          if (!(error instanceof SessionExpiredException)) {
            handleFileException(error);
          }
          handleLoadingScreen(false, '');
        }
      }

      if (kmzFiles.length > 0) {
        try {
          const formData = new FormData();

          kmzFiles.forEach((file: File) => {
            formData.append('kmz', file);
          });

          formData.append('id', land.id);

          const requestRef: RequestWrapper = patch({
            url: '/land/importKmz',

            params: formData,

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          responseKMZ = await requestRef;

          if (!responseKMZ?.body.error) {
            handleModal(responseKMZ?.body.code);

            successRequest = true;

            responseKMZ?.body.land.landFields.forEach((landField, index) => {
              const polygon = landField.polygon.map((element) => ([element.lat, element.lon]));
              const polygonCenter = polylabel([polygon], 1.0);

              landShapes.push({
                key: `shapeland_${index + landShapes.length}`,
                origin: 'shapeland',
                polygonCenter,
                area: calculatePolygonAreaInHectare(landField.polygon),
                isDeleted: false,
                isSelected: false,
                polygon,
              });
            });

            responseKMZ?.body.geometriesOutput.forEach((shape, index) => {
              const polygon = shape.coordinates.map((element) => ([element.lat, element.lon]));

              const polygonCenter = polylabel([polygon], 1.0);

              let foundCulture;
              let finalCultureString;
              let finalCultureId;

              if (responseKMZ?.body.cultureList[index] != null && responseKMZ?.body.cultureList[index] != '') {
                foundCulture = newCulturesOptions.find(
                  (element) => element.description
                    .toLowerCase() === responseKMZ?.body.cultureList[index].toLowerCase(),
                );
              }

              if (foundCulture) {
                finalCultureString = `::${foundCulture.id}::${foundCulture.description}`;
                finalCultureId = foundCulture.id;
              }

              newFileShapes.push({
                key: shape.temporaryId + newFileShapes.length,
                origin: 'shapefile',
                polygonCenter,
                area: calculatePolygonAreaInHectare(shape.coordinates),
                isDeleted: false,
                isSelected: false,
                polygon,

                form: {
                  name: {
                    value: responseKMZ?.body.landFieldNameList[index] || '',
                    error: '',
                  },

                  culture: {
                    value: responseKMZ?.body.cultureList[index] ? finalCultureString : '',
                    id: responseKMZ?.body.cultureList[index] ? finalCultureId : null,
                    error: '',
                  },

                  details: {
                    value: responseKMZ?.body.noteList[index] || '',
                  },
                },
              });
            });

            filesSuccess.push(responseKMZ?.body.fileSuccess);
          }
        } catch (error: unknown) {
          if (!(error instanceof SessionExpiredException)) {
            handleFileException(error);
          }
          handleLoadingScreen(false, '');
        }
      }

      const suc = [];
      const fail = [];

      for (const file in filesSuccess) {
        if (filesSuccess[file][0].status) {
          suc.push(filesSuccess[file][0]);
        } else {
          fail.push(filesSuccess[file][0]);
        }
      }

      const newShapes = [...landShapes, ...newFileShapes];
      for (let iCount = 0; iCount < newShapes.length; iCount++) {
        const shape = newShapes[iCount];
        if (!checkIfAPolygonIsSimple(shape.polygon)) {
          notification.error({
            message: 'Não foi possível importar os talhões',
            description: 'Pelo menos um dos talhões importados é inválido. As bordas do talhão não podem se cruzar.',
          });
          handleLoadingScreen(false, '');
          return;
        }
      }

      setFileSuccess(suc);
      setFileErrors(fail);
      setFileShapes(newFileShapes);
      setShapes(newShapes);
      setMapCenter(newFileShapes.length ? newFileShapes[0].polygonCenter : land.location);

      handleLoadingScreen(false, '');

      if (successRequest) {
        handleStateField('stage', 2);
      }
    }
  };

  // Altera o centro do mapa quando for para a etapa 3
  // ou quando o Panel do Collapse é clicado
  const checkMapCenter = (polygonCenter): any => {
    const newMapCenter = [];

    if (polygonCenter[0] === mapCenter[0]) {
      const lat = polygonCenter[0] + 0.0000000000001;
      const lon = polygonCenter[1] + 0.0000000000001;

      newMapCenter.push(lat, lon);

      return newMapCenter;
    }

    return polygonCenter;
  };

  const onSubmitToThirdStage = async (): Promise<void> => {
    const firstActiveShape = fileShapes.find(
      (element) => !element.isDeleted && element.origin === 'shapefile',
    );

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!firstActiveShape) {
      notification.warning({
        message: 'Todos os talhões foram removidos',
        description: 'Selecione pelo menos um talhão para continuar.',
        icon: (<WarningOutlined style={{ color: '#faad14' }} />),
      });
    } else {
      handleLoadingScreen(true, 'Carregando...');

      try {
        const requestRef: RequestWrapper = get({
          url: `/land/retrieveCulturesByLand?landId=${land.id}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestRef;

        const newCulturesOptions = [];

        shapes.forEach((element, index) => {
          firstActiveShape.isSelected = true;

          if (element.key !== firstActiveShape.key) {
            shapes[index].isSelected = false;
          }
        });

        response?.body.cultures.forEach((culture) => newCulturesOptions.push(culture));

        setCulturesOptions(newCulturesOptions);
        setShapes(shapes);
        setClickedShape(firstActiveShape);
        setStage(3);
        setZoom(17);
        setMapCenter(checkMapCenter(firstActiveShape.polygonCenter));
      } catch (error: unknown) {
        if (!(error instanceof SessionExpiredException)) {
          message.error('Erro ao carregar as opções de culturas');
        }
      } finally {
        handleLoadingScreen(false, '');
      }
    }
  };

  const onSubmitToFinish = async (): Promise<void> => {
    let invalidFormFields = false;
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    fileShapes.forEach((shape, index) => {
      if (!shape.isDeleted) {
        if (!shape.form.name.value) {
          fileShapes[index].form.name.error = 'Campo obrigatório';
          invalidFormFields = true;
        }

        if (!shape.form.culture.value) {
          fileShapes[index].form.culture.error = 'Campo obrigatório';
          invalidFormFields = true;
        }
      }
    });

    if (invalidFormFields) {
      notification.warning({
        message: 'Campo obrigatório',
        description: 'Verifique se os nomes e as culturas dos talhões estão preenchidos.',
        icon: (<WarningOutlined style={{ color: '#faad14' }} />),
      });

      setFileShapes(fileShapes);
    } else {
      let successRequest = false;
      const activeShapes = fileShapes.filter((element) => !element.isDeleted);

      const params = {
        landFields: activeShapes.map((element) => ({
          coordinates: element
            .polygon
            .map((coordinate) => ({ lat: coordinate[0], lon: coordinate[1] })),

          name: element.form.name.value,
          area: element.area,
          culture: element.form.culture.id,
          details: element.form.details.value,
        })),

        landId: +land.id,
      };

      handleLoadingScreen(true, 'Salvando...');

      try {
        const createShapesReqRef: RequestWrapper = post({
          url: '/land/createShapefilesLandFields',

          params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await createShapesReqRef;

        if (!response?.body.error) {
          successRequest = true;

          notification.success({
            message: 'Sucesso',
            description: 'Talhões salvos com sucesso.',
          });
        }
      } catch (error: unknown) {
        if (!(error instanceof SessionExpiredException)) {
          notification.error({
            message: 'Não foi possível concluir a requisição',
            description: 'Ocorreu um erro ao salvar as informações dos talhões.',
          });
        }
      } finally {
        handleLoadingScreen(false, '');
      }

      if (successRequest) {
        navigate('/home');
      }
    }
  };

  const getProperties = async (matriculationParameter): Promise<void> => {
    const url = `/admin/findMatriculationsWithLands?bp=${matriculationParameter}`;
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const requestRef: RequestWrapper = get({
        url,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestRef;

      const newLandOptions = [];

      if (!response?.body.error) {
        const { matriculations } = response?.body;

        matriculations.forEach((element) => {
          element.lands.forEach((landP) => {
            newLandOptions.push({ ...landP, matriculationParameter: element.id });
          });
        });

        setLandOptions(newLandOptions);
      } else {
        message.error('Nenhuma propriedade encontrada');
      }
    } catch (error: unknown) {
      if (!(error instanceof SessionExpiredException)) {
        message.error('Erro ao carregar as propriedades');
      }
    }
  };

  // Altera o form do fileShapes - Name, Culture e Details
  const handleFileShapesForm = (index, field, value): void => {
    fileShapes[index].form[field].value = value;
    fileShapes[index].form[field].error = '';

    if (!value) {
      fileShapes[index].form[field].error = 'Campo obrigatório.';
    }

    if (field === 'name') {
      if (value.length > 150) {
        fileShapes[index].form[field].error = 'Máximo de 150 caracteres.';
      }

      if (value.length > 0 && value.length < 3) {
        fileShapes[index].form[field].error = 'Mínimo 3 caracteres.';
      }
    }

    if (field === 'culture') {
      const cultureId = value.split('::')[1];

      fileShapes[index].form[field].id = cultureId;
    }

    if (field === 'details' && value.length > 1000) {
      fileShapes[index].form[field].error = 'Máximo de 1000 caracteres.';
    }
    setFileShapes(fileShapes);
  };

  // Altera o talhão selecionado, o zoom do mapa e o centro
  const handleClickedShapeInThirdStage = (clickedShapeP): void => {
    shapes.forEach((element, index) => {
      clickedShapeP.isSelected = true;

      if (element.key !== clickedShapeP.key) {
        shapes[index].isSelected = false;
      }
    });

    setShapes(shapes);
    setClickedShape(clickedShapeP);
    setZoom(zoom === 17 ? 17.1 : 17);
    setMapCenter(checkMapCenter(clickedShapeP.polygonCenter));
  };

  // Quando o usuário clicar em algum Panel do Collapse
  // ou clicar nos botões Voltar ou Próximo
  // é alterado o talhão selecionado, o zoom do mapa e o centro.
  const handleClickedShapeCollapse = (buttonActionType, shapeKey, isNext): void => {
    let newClickedShape = {};

    // Quando o usuário clica em algum Panel
    if (buttonActionType === 'collapsible') {
      if (!shapeKey) {
        shapes.forEach((_, index) => {
          shapes[index].isSelected = false;
        });

        setShapes(shapes);
        setClickedShape({});
      } else {
        newClickedShape = fileShapes.find((element) => element.key === +shapeKey);

        handleClickedShapeInThirdStage(newClickedShape);
      }
    }

    // Quando o usuário clica nos botões Voltar ou Próximo.
    if (buttonActionType === 'nextOrPrevious') {
      // IF - Clicar em Próximo - busca o shape com id maior e não deletado.
      // ELSE - Clicar em Voltar - busca o shape com id menor e não deletado.
      if (isNext) {
        newClickedShape = fileShapes
          .find((element) => element.key > shapeKey && !element.isDeleted);
      } else {
        newClickedShape = fileShapes
          .sort((a, b) => b.key - a.key)
          .find((element) => element.key < shapeKey && !element.isDeleted);
        fileShapes.sort((a, b) => a.key - b.key);
      }

      // Se newClickedShape existir então o Collapse e os dados do Mapa são alterados
      // para o talhão encontrado (no caso Voltar ou Próximo).
      if (newClickedShape) {
        handleClickedShapeInThirdStage(newClickedShape);
      }

      // Se não achar nenhum talhão e o botão clicado foi o Próximo,
      // significa que foi o último. Então a requsição de Concluir importação é chamada.
      if (!newClickedShape && isNext) {
        onSubmitToFinish();
      }
    }
  };

  const handleMatriculation = async (text): Promise<void> => {
    let result: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const value = text.replace(/\D/g, ''); // Only numbers;

    matriculation.error = false;
    matriculation.errorMessage = '';
    matriculation.status = '';
    matriculation.value = value;

    if (value.length === 10) {
      setValidatingMatriculation(true);

      try {
        // result = await checkMatriculation(value);
        const url = `/admin/validateBp?bp=${value}`;

        const requestRef: RequestWrapper = get({
          url,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        result = await requestRef;

        if (result?.body.code === 17000) {
          matriculation.error = false;
          matriculation.errorMessage = '';
          matriculation.status = 'success';
          getProperties(value);
        }
      } catch (error: unknown) {
        if (!(error instanceof SessionExpiredException)) {
          const exp = error.response.body;

          switch (true) {
            case (exp.code === -18000):
              // Bp not found of SAP
              matriculation.error = true;
              matriculation.errorMessage = 'BP não encontrado. Verifique e tente novamente.';
              matriculation.status = 'error';
              break;

            case (exp.code === -18002):
              // "MatriculationBpNotFound", -18002
              notification.error({
                message: t('error', { ns: namespaces.common }),
                description: backendResponseCodes.Error.BP
                  .MatriculationBpNotFound.description.pt_br,
              });
              break;

            default:
              // generic
              matriculation.error = true;
              matriculation.errorMessage = 'Ocorreu um erro ao validar o BP.';
              matriculation.status = 'error';
              break;
          }
        }
      }
    } else {
      setLandOptions([]);
      setLand((previous) => {
        return ({
          ...previous,
          value: '',
        });
      });
    }

    setMatriculation(matriculation);
    setValidatingMatriculation(false);
  };

  const handleLand = (text): void => {
    const landId = text.split('::')[1];
    const landName = text.split('::')[2];

    const { location: locationOject } = landOptions.find((element) => element.id === +landId);

    setLand({
      id: landId,
      value: landName,
      location: [locationOject.lat, locationOject.lon],
      error: false,
      errorMessage: '',
      status: '',
    });
  };

  const removeFile = (file): void => {
    const newAllFiles = allFiles.filter((element) => element.name !== file.name);

    setAllfiles(newAllFiles);
  };

  const changeUpload = ({ file }): void => {
    if (file.status === 'uploading') {
      const sameName = allFiles.find((element) => element.name === file.name);

      const notificationArguments = {};

      if (sameName) {
        notificationArguments.error = true;
        notificationArguments.message = 'Arquivo já carregado';
        notificationArguments.description = `O arquivo ${file.name} já foi carregado.`;
      }

      if (file.size > 10000000) {
        notificationArguments.error = true;
        notificationArguments.message = 'Tamanho do arquivo excedido';
        notificationArguments.description = `O tamanho do arquivo ${file.name} ultrapassou 10 MB.`;
      }

      if (notificationArguments.error) {
        notification.error(notificationArguments);
      } else {
        setAllfiles([...allFiles, file.originFileObj]);
        setErrorShapefiles(false);
      }
    }
  };

  const renderFileName = (text): any => {
    if (text === undefined) {
      return '';
    }
    if (text.length > 20) {
      return `${text.slice(0, 15)} (...)`;
    }

    return text;
  };

  useEffect(() => {
    return () => {
      setState(false);
    };
  }, []);

  return (
    <div className="ImportShapefiles">
      <Spin tip={loadingScreen.text} spinning={loadingScreen.active}>
        <ImportShapefilesHeader
          stage={stage}
          handleModal={handleModal}
          onSubmitToSecondStage={onSubmitToSecondStage}
          onSubmitToThirdStage={onSubmitToThirdStage}
          onSubmitToFinish={onSubmitToFinish}
          handleStateField={handleStateField}
        />
        <Card>
          <Form>
            <Row>
              <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: stage === 3 ? 10 : 6 }}>
                {
                  stage === 1 && (
                    <ImportShapefilesFirstStage
                      matriculation={matriculation}
                      land={land}
                      landOptions={landOptions}
                      allFiles={allFiles}
                      errorShapefiles={errorShapefiles}
                      validatingMatriculation={validatingMatriculation}
                      renderFileName={renderFileName}
                      removeFile={removeFile}
                      changeUpload={changeUpload}
                      handleMatriculation={handleMatriculation}
                      handleLand={handleLand}
                    />
                  )
                }
                {
                  stage === 2 && (
                    <ImportShapefilesSecondStage
                      matriculation={matriculation}
                      land={land}
                      fileSuccess={fileSuccess}
                      fileErrors={fileErrors}
                      renderFileName={renderFileName}
                    />
                  )
                }
                {
                  stage === 3 && (
                    <ImportShapefilesThirdStage
                      matriculation={matriculation}
                      culturesOptions={culturesOptions}
                      land={land}
                      fileShapes={fileShapes}
                      clickedShape={clickedShape}
                      handleFileShapesForm={handleFileShapesForm}
                      handleClickedShapeCollapse={handleClickedShapeCollapse}
                    />
                  )
                }
              </Col>

              <Col xs={{ span: 24 }} md={{ span: 24 }} lg={{ span: stage === 3 ? 14 : 18 }}>
                {
                  stage !== 1 && (
                    <GeoMapShapefiles
                      stage={stage}
                      zoom={zoom}
                      mapCenter={mapCenter}
                      clickedShape={clickedShape}
                      shapes={shapes}
                      handleStateField={handleStateField}
                      land={land}
                    />
                  )
                }
              </Col>
            </Row>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

ImportShapefiles.propTypes = {};
ImportShapefiles.defaultProps = {};

export default ImportShapefiles;
